<script setup lang="ts">
  import { computed, defineAsyncComponent } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRoute } from 'vue-router'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import RemoveWindow from '@/shared/components/rebranding/RemoveWindow'
  import { PurchaseFlowRoutes } from '@/shared/types/router.types'

  interface Props {
    btnText: string
    wide?: boolean
    showRemoveButton: boolean
    showRemoveWindow: boolean
    privacyPolicyLink: string
  }

  defineProps<Props>()
  const emit = defineEmits<{
    (e: 'submit'): void
    (e: 'open-remove'): void
    (e: 'close-remove'): void
    (e: 'delete'): void
    (e: 'track-privacy-policy'): void
  }>()

  const { locale } = useI18n()
  const route = useRoute()

  const btnSymbol = computed<'arrow-narrow-right' | 'arrow-narrow-left' | undefined>(() => {
    if (route.name !== PurchaseFlowRoutes.HOLDER_INFO) return undefined
    return locale.value === 'en' ? 'arrow-narrow-right' : 'arrow-narrow-left'
  })

  const VisitorsReminder = defineAsyncComponent(
    () => import('@/shared/components/rebranding/atoms/VReminder.vue')
  )
</script>

<template>
  <div class="visitor-form__footer">
    <ButtonQC
      :icon-after-content="btnSymbol"
      :wide="wide"
      data-test="proceed-button"
      @click="emit('submit')"
    >
      {{ btnText }}
    </ButtonQC>

    <template v-if="showRemoveButton">
      <Transition name="fade" mode="out-in">
        <ButtonQC
          v-if="!showRemoveWindow"
          variant="outline"
          icon-before-content="delete-bin"
          size="large"
          data-test="remove-user-button"
          @click="emit('open-remove')"
        >
          {{ $t('personal_info.additional.delete') }}
        </ButtonQC>
        <RemoveWindow
          v-else
          @cancel="emit('close-remove')"
          @delete="emit('delete')"
        />
      </Transition>
    </template>

    <VisitorsReminder
      is-privacy-policy
      :privacy-policy-link="privacyPolicyLink"
      link-test-id="privacy-policy-link"
      @track-privacy-policy="emit('track-privacy-policy')"
    />
  </div>
</template>

<style lang="scss" scoped>
  .visitor-form__footer {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-3);
  }
  .fade {
    &-enter-active,
    &-leave-active {
      transition: opacity 0.15s ease;
    }
    &-enter-from,
    &-leave-to {
      opacity: 0;
    }
  }
</style>
